import {
  LinearMipmapLinearFilter,
  RepeatWrapping,
  SRGBColorSpace,
  TextureLoader,
  type Texture,
} from 'three';

/**
 * PBR textury povrchu (CC0, ambientCG — viz public/textures/terrain/CREDITS.md).
 *
 * Pět vrstev: tráva, skála, hlína, písek, sníh. Barvy a normály jsou samostatné
 * 1K JPG, ale **mapy drsnosti jsou sloučené do jedné RGBA textury**
 * (R=tráva, G=skála, B=hlína, A=sníh) — z osmi texture fetchů na pixel
 * se tím stanou dva.
 */
export interface TerrainTextureSet {
  grassColor: Texture;
  grassNormal: Texture;
  rockColor: Texture;
  rockNormal: Texture;
  dirtColor: Texture;
  dirtNormal: Texture;
  sandColor: Texture;
  sandNormal: Texture;
  snowColor: Texture;
  snowNormal: Texture;
  roughPacked: Texture;
  dispose(): void;
}

const BASE = 'textures/terrain';

/**
 * Maximální anizotropie bez zakládání celého WebGLRendereru — textury se
 * načítají dřív, než engine vůbec vznikne.
 */
function maxAnisotropy(): number {
  const canvas = document.createElement('canvas');
  const gl = canvas.getContext('webgl2');
  if (!gl) return 1;
  const ext = gl.getExtension('EXT_texture_filter_anisotropic');
  const value = ext ? (gl.getParameter(ext.MAX_TEXTURE_MAX_ANISOTROPY_EXT) as number) : 1;
  gl.getExtension('WEBGL_lose_context')?.loseContext();
  return value;
}

export async function loadTerrainTextures(
  onProgress?: (loaded: number, total: number) => void,
): Promise<TerrainTextureSet> {
  const loader = new TextureLoader();
  // Anizotropní filtrování je u terénu zásadní: bez něj se dlaždice
  // v ostrém úhlu k pohledu rozmažou do šedi.
  const anisotropy = Math.min(8, maxAnisotropy());

  const files = [
    ['grassColor', 'grass_color.webp', true],
    ['grassNormal', 'grass_normal.webp', false],
    ['rockColor', 'rock_color.webp', true],
    ['rockNormal', 'rock_normal.webp', false],
    ['dirtColor', 'dirt_color.webp', true],
    ['dirtNormal', 'dirt_normal.webp', false],
    ['sandColor', 'sand_color.webp', true],
    ['sandNormal', 'sand_normal.webp', false],
    ['snowColor', 'snow_color.webp', true],
    ['snowNormal', 'snow_normal.webp', false],
    ['roughPacked', 'rough_packed.webp', false],
  ] as const;

  let loaded = 0;
  const entries = await Promise.all(
    files.map(async ([key, file, srgb]) => {
      const texture = await loader.loadAsync(`${BASE}/${file}`);
      texture.wrapS = RepeatWrapping;
      texture.wrapT = RepeatWrapping;
      texture.anisotropy = anisotropy;
      texture.minFilter = LinearMipmapLinearFilter;
      texture.generateMipmaps = true;
      // Barvy jsou v sRGB, normály a drsnost musí zůstat lineární,
      // jinak by se hodnoty ohnuly gama křivkou.
      if (srgb) texture.colorSpace = SRGBColorSpace;
      loaded++;
      onProgress?.(loaded, files.length);
      return [key, texture] as const;
    }),
  );

  const set = Object.fromEntries(entries) as unknown as TerrainTextureSet;
  set.dispose = () => {
    for (const [, texture] of entries) texture.dispose();
  };
  return set;
}
